/**
 * Owner: Masa Bayu
 * File: qx-editor-page-face.tsx
 * Date:Time: 2026-10-14 04:05
 * Description: Fasa 20–27 · Face layers · R10.4 body pipe wire.
 */

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type RefObject,
} from 'react';
import { HighlightOverlay } from '../interaction/HighlightOverlay';
import { textElementsToHitAtoms } from '../interaction/text-element-to-hit-atom';
import { useTextSelection } from '../interaction/useTextSelection';
import { reconcileTextAndSoftmasks } from '../extract/spsu-extractor';
import { loadAssetFonts } from '../fonts/spsu-fonts';
import { LiveObject } from './qx-editor-page-face-objects';
import { renderBodyLayer } from './render-body-layer';
import {
  detectNativeDropCapPairs,
} from './spsu-native-dropcap-face';
import { classifyPageToFaceLayers } from './spsu-structural-bridge';
import { isTextLinePlate } from './spsu-ocr-line-plates-core';
import type {
  AssetLibrary,
  BBox,
  ImageElement,
  SPSUPage,
  TextElement,
} from '../extract/spsu-types';

export type QxEditorPageFaceSpsuProps = {
  readonly page: SPSUPage;
  readonly assets: AssetLibrary;
  readonly editable?: boolean;
  readonly scale?: number;
  readonly fit?: boolean;
  readonly onTextChange?: (elementId: string, newContent: string) => void;
  readonly onElementMove?: (elementId: string, newBBox: BBox) => void;
};

function unitOf(page: SPSUPage): string {
  return page.size.unit;
}

function useFitScale(
  page: SPSUPage,
  enabled: boolean,
): { readonly hostRef: RefObject<HTMLDivElement | null>; readonly scale: number } {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    if (!enabled) {
      setScale(1);
      return;
    }
    const host = hostRef.current;
    if (!host) return;

    const measure = (): void => {
      const availW = host.clientWidth;
      const availH = host.clientHeight;
      if (availW <= 0) return;
      const probe = document.createElement('div');
      probe.style.width = `${page.size.width}${page.size.unit}`;
      probe.style.height = `${page.size.height}${page.size.unit}`;
      probe.style.position = 'absolute';
      probe.style.visibility = 'hidden';
      probe.style.pointerEvents = 'none';
      host.appendChild(probe);
      const naturalW = probe.offsetWidth;
      const naturalH = probe.offsetHeight;
      host.removeChild(probe);
      if (naturalW <= 0 || naturalH <= 0) return;
      const scaleW = availW / naturalW;
      const scaleH = availH > 0 ? availH / naturalH : scaleW;
      setScale(Math.min(scaleW, scaleH));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    return () => ro.disconnect();
  }, [enabled, page.size.width, page.size.height, page.size.unit]);

  return { hostRef, scale };
}

function renderAbsoluteTexts(
  texts: readonly TextElement[],
  assets: AssetLibrary,
  editable: boolean,
  onTextChange: QxEditorPageFaceSpsuProps['onTextChange'],
  skip: ReadonlySet<string>,
): ReactElement[] {
  return texts
    .filter((el) => !skip.has(el.id))
    .map((element) => (
      <LiveObject
        key={element.id}
        element={element}
        assets={assets}
        editable={editable}
        {...(onTextChange ? { onTextChange } : {})}
      />
    ));
}

/**
 * Fasa 14–27 · visual · structural · body · highlight overlay · header/footer.
 */
export function QxEditorPageFaceSpsu({
  page,
  assets,
  editable = false,
  scale = 1,
  fit = false,
  onTextChange,
}: QxEditorPageFaceSpsuProps): ReactElement {
  const unit = unitOf(page);
  const { hostRef, scale: fitScaleFromHost } = useFitScale(page, fit);
  const zoom = scale > 0 ? scale : 1;
  const fitScale = fit ? fitScaleFromHost * zoom : zoom;
  const [, setFontsEpoch] = useState(0);
  useEffect(() => {
    let cancelled = false;
    void loadAssetFonts(assets).then(() => {
      if (!cancelled) setFontsEpoch((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [assets]);

  const images = page.elements.filter((el): el is ImageElement => el.type === 'image');
  const texts = page.elements.filter((el): el is TextElement => el.type === 'text');
  const reconciled = editable
    ? { images: [...images], texts: [...texts] }
    : reconcileTextAndSoftmasks(images, texts, assets);

  const layers = classifyPageToFaceLayers(
    page,
    [...reconciled.images, ...reconciled.texts],
    assets,
  );

  const structuralTexts = [...layers.table, ...layers.list];
  const structuralIds = new Set(structuralTexts.map((el) => el.id));
  const bodyTexts = layers.body.filter((el) => !structuralIds.has(el.id));
  /**
   * Selectable atoms = body + structural absolute text (table/list Face).
   * P19 currently parks many body lines in tableLayer · still live author text.
   */
  const selectableAtoms = useMemo(() => {
    const skip = new Set(
      [...layers.table, ...layers.list].map((el) => el.id),
    );
    const bodyOnly = layers.body.filter((el) => !skip.has(el.id));
    const merged = [...bodyOnly, ...layers.table, ...layers.list].sort(
      (a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x,
    );
    return textElementsToHitAtoms(merged);
  }, [layers.body, layers.table, layers.list]);
  const { highlightBoxes, sheetRef } = useTextSelection(selectableAtoms, {
    width: page.size.width,
    height: page.size.height,
  });
  const dropCapPairs = detectNativeDropCapPairs(layers.body);
  /** WS-9 defense-in-depth · never paint text-line plates over justified body. */
  const safeVisualLayer = layers.visual.filter((element) => {
    const className = (element as ImageElement & { className?: string })
      .className;
    if (
      typeof className === 'string' &&
      (className.includes('plate-line') || className.includes('plate_line'))
    ) {
      console.warn('[WS-9 Face guard] Dropped plate-line element:', element.id);
      return false;
    }
    if (isTextLinePlate(element, assets[element.assetId]?.paintTone)) {
      console.warn('[WS-9 Face guard] Dropped text-line plate:', element.id);
      return false;
    }
    return true;
  });

  const face = (
    <div
      className="qx-editor-page-face qx-page-canvas"
      data-page-id={page.id}
      data-page-number={page.pageNumber}
      data-qxeditor-page-face=""
      data-fasa14-layered=""
      data-fasa20-universal-pipeline=""
      data-fasa23-structural=""
      data-fasa27-interaction=""
      data-qx-no-plate-paint="true"
      data-fit-scale={fitScale}
      data-layer-visual={String(safeVisualLayer.length)}
      data-layer-structural={String(structuralTexts.length)}
      data-layer-table={String(layers.table.length)}
      data-layer-list={String(layers.list.length)}
      data-layer-header={String(layers.header.length)}
      data-layer-footer={String(layers.footer.length)}
      data-layer-body={String(layers.body.length)}
      data-layer-highlight={String(highlightBoxes.length)}
      data-native-dropcap-pairs={String(dropCapPairs.length)}
      data-native-dropcap-justified={dropCapPairs.length > 0 ? 'true' : 'false'}
      aria-label={`Page ${page.pageNumber}`}
      style={{
        position: 'relative',
        width: `${page.size.width * fitScale}${unit}`,
        height: `${page.size.height * fitScale}${unit}`,
        /* `line-height: 0` lazim pada pembalut imej (buang jurang baseline) ·
         * ia diwarisi semua objek teks dan meruntuhkan perenggan multi-baris
         * menjadi satu baris bertindih. Akar Face kebal daripada itu. */
        lineHeight: 'normal',
        overflow: 'hidden',
        flex: '0 0 auto',
        margin: 0,
      }}
    >
      <div
        ref={sheetRef}
        className="qx-editor-page-face-sheet"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: `${page.size.width}${unit}`,
          height: `${page.size.height}${unit}`,
          transform: fitScale === 1 ? undefined : `scale(${fitScale})`,
          transformOrigin: 'top left',
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
          userSelect: 'none',
        }}
      >
        <div
          className="qx-layer-visual"
          data-qx-layer="visual"
          style={{ position: 'absolute', inset: 0, zIndex: 0 }}
        >
          {safeVisualLayer.map((element) => (
            <LiveObject
              key={element.id}
              element={element}
              assets={assets}
              editable={false}
            />
          ))}
        </div>

        {/* Fasa 23 · table/list absolute · FORBIDDEN from JustifiedTextBlock / Knuth */}
        <div
          className="qx-layer-structural"
          data-qx-layer="structural"
          data-table-count={String(layers.table.length)}
          data-list-count={String(layers.list.length)}
          style={{ position: 'absolute', inset: 0, zIndex: 4 }}
        >
          {layers.table.map((element) => (
            <LiveObject
              key={element.id}
              element={element}
              assets={assets}
              editable={editable}
              {...(onTextChange ? { onTextChange } : {})}
            />
          ))}
          {layers.list.map((element) => (
            <LiveObject
              key={element.id}
              element={element}
              assets={assets}
              editable={editable}
              {...(onTextChange ? { onTextChange } : {})}
            />
          ))}
        </div>

        <div
          className="qx-layer-body"
          data-qx-layer="body"
          style={{ position: 'absolute', inset: 0, zIndex: 5 }}
        >
          {renderBodyLayer(
            bodyTexts,
            assets,
            editable,
            onTextChange,
            page.size.width,
            page.size.height,
          )}
        </div>

        {/* Fasa 27 · Engine 5 highlight · pointer-events none · z 6 */}
        <HighlightOverlay boxes={highlightBoxes} />

        <div
          className="qx-layer-header"
          data-qx-layer="header"
          style={{ position: 'absolute', inset: 0, zIndex: 10 }}
        >
          {renderAbsoluteTexts(layers.header, assets, editable, onTextChange, structuralIds)}
        </div>

        <div
          className="qx-layer-footer"
          data-qx-layer="footer"
          style={{ position: 'absolute', inset: 0, zIndex: 10 }}
        >
          {renderAbsoluteTexts(layers.footer, assets, editable, onTextChange, structuralIds)}
        </div>
      </div>
    </div>
  );

  if (!fit) return face;

  return (
    <div
      ref={hostRef}
      className="qx-editor-page-face-fit"
      data-qxeditor-page-face-fit=""
      style={{
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        maxHeight: '100%',
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
      }}
    >
      {face}
    </div>
  );
}

export default QxEditorPageFaceSpsu;
